import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal, Select, Spin, message } from 'antd';
import { dviApi, dviTemplateApi } from '../../services/api/dviApi';
import { DVIInspectionFull, DVIInspectionItem, DVIItemStatus, DVITemplate } from '../../types/dvi';
import { DVIInspectionForm } from './DVIInspectionForm';
import JobTemplatePickerModal from './JobTemplatePickerModal';
import UnassignedPhotoTray from './UnassignedPhotoTray';
import { useCapability } from '../auth/RoleGuard';

export interface EstimateInspectionSectionProps {
  estimateId: number;
  // vehicle is passed by EstimateForm but is not yet used in this section.
  // Kept in the interface for future use (e.g. pre-selecting template by vehicle type).
  vehicle?: any;
  /** Called after a DVI concern is successfully converted to a line item so the
   *  parent estimate can reload its Service Items immediately (bugfix: line item
   *  was previously only visible after a full page refresh). */
  onLineItemsChanged?: () => void;
}

const EstimateInspectionSection: React.FC<EstimateInspectionSectionProps> = ({
  estimateId,
  onLineItemsChanged,
}) => {
  const canBypass = useCapability('bypass_inspection');
  const [inspection, setInspection] = useState<DVIInspectionFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<DVITemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  // WP-09 — the finding whose job template is being chosen. "Add to estimate"
  // opens the picker instead of silently creating a bare, unpriced line.
  const [jobPickerItem, setJobPickerItem] = useState<DVIInspectionItem | null>(null);

  const refetch = useCallback(async () => {
    if (estimateId <= 0) return;
    try {
      const insp = await dviApi.getByEstimate(estimateId);
      setInspection(insp);
    } catch (err: any) {
      message.error(`Failed to reload inspection: ${err?.message ?? 'unknown'}`);
    }
  }, [estimateId]);

  useEffect(() => {
    if (estimateId <= 0) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [insp, tpls] = await Promise.all([
          dviApi.getByEstimate(estimateId),
          dviTemplateApi.list(),
        ]);
        if (!cancelled) {
          setInspection(insp);
          setTemplates(tpls);
          // Default selected template: is_default first, then first in list
          const def = tpls.find(t => t.is_default) ?? tpls[0];
          if (def) setSelectedTemplateId(def.id);
        }
      } catch (err: any) {
        if (!cancelled) message.error(`Failed to load inspection: ${err?.message ?? 'unknown'}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [estimateId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const insp = await dviApi.startForEstimate(estimateId, selectedTemplateId);
      setInspection(insp);
    } catch (err: any) {
      message.error(`Failed to start inspection: ${err?.message ?? 'unknown'}`);
    } finally {
      setStarting(false);
    }
  };

  const handleChangeTemplate = () => {
    if (!inspection) return;
    Modal.confirm({
      title: 'Change template?',
      content: 'This clears the current inspection entries & photos.',
      onOk: async () => {
        try {
          await dviApi.delete(inspection.id);
          setInspection(null);
          // Reset selected template back to the default
          const def = templates.find(t => t.is_default) ?? templates[0];
          if (def) setSelectedTemplateId(def.id);
        } catch (err: any) {
          message.error(`Failed to clear inspection: ${err?.message ?? 'unknown'}`);
        }
      },
    });
  };

  // Unsaved estimate — show placeholder
  if (estimateId <= 0) {
    return (
      <Card title="Inspection & Photos" style={{ marginTop: 12 }}>
        <span style={{ color: '#888' }}>
          Save the estimate to start the inspection &amp; add photos.
        </span>
      </Card>
    );
  }

  const cardTitle = inspection
    ? `Inspection & Photos${inspection.template_name ? ` · ${inspection.template_name}` : ''}`
    : 'Inspection & Photos';

  return (
    <Card
      title={cardTitle}
      style={{ marginTop: 12 }}
      extra={
        inspection ? (
          <Button type="link" size="small" onClick={handleChangeTemplate}>
            Change template
          </Button>
        ) : null
      }
    >
      {loading ? (
        <Spin />
      ) : !inspection ? (
        templates.length === 0 ? (
          <span style={{ color: '#888' }}>No inspection templates configured.</span>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select
              value={selectedTemplateId}
              onChange={(val) => setSelectedTemplateId(val)}
              options={templates.map(t => ({ value: t.id, label: t.name }))}
              style={{ minWidth: 160 }}
            />
            <Button type="primary" onClick={handleStart} loading={starting}>
              Start inspection
            </Button>
          </div>
        )
      ) : (
        <>
          <DVIInspectionForm
            inspection={inspection}
            canBypass={canBypass}
            onItemStatusChange={async (
              itemId: number,
              status: Exclude<DVIItemStatus, 'pending'>,
              notes?: string | null,
            ) => {
              await dviApi.updateItem(inspection.id, itemId, { status, notes });
              await refetch();
            }}
            onSummaryChange={(notes: string) => {
              dviApi.updateSummary(inspection.id, notes);
            }}
            onComplete={async () => {
              try {
                await dviApi.complete(inspection.id);
                await refetch();
              } catch (err: any) {
                message.error(err.response?.data?.error ?? 'Cannot complete');
              }
            }}
            // WP-09 — open the job picker rather than converting immediately.
            // The bare conversion is still reachable from inside the picker, so
            // an unconfigured shop is not blocked.
            onConvert={(item: DVIInspectionItem) => setJobPickerItem(item)}
            onReadingChange={(itemId, val) => {
              dviApi.updateReading(inspection.id, itemId, val).then(() => refetch());
            }}
            // WP-07 — a note is an observation about the item, so it saves on
            // its own: no status required, no photo required.
            onNoteChange={async (itemId, fields) => {
              await dviApi.updateItemNote(inspection.id, itemId, fields);
              await refetch();
            }}
            onAddItem={async (input) => {
              await dviApi.addItem(inspection.id, input);
              await refetch();
            }}
          />
          <UnassignedPhotoTray
            inspectionId={inspection.id}
            points={inspection.items.map(i => ({ id: i.id, point_name: i.point_name }))}
            onAssigned={refetch}
          />
          {jobPickerItem && (
            <JobTemplatePickerModal
              open
              inspectionId={inspection.id}
              item={jobPickerItem}
              estimateId={estimateId}
              onCancel={() => setJobPickerItem(null)}
              onApplied={async ({ duplicate, warnings }) => {
                setJobPickerItem(null);
                // A re-apply says so plainly. Silently reporting "added" when
                // nothing was added is how a technician ends up adding it a
                // third time looking for the line.
                if (duplicate) {
                  message.info('Already on this estimate — nothing added.');
                } else {
                  message.success('Job added to the estimate');
                }
                warnings.forEach((w) => message.warning(w));
                await refetch();
                onLineItemsChanged?.();
              }}
              onAddWithoutTemplate={async () => {
                await dviApi.convertToLineItem(inspection.id, jobPickerItem.id, {
                  lineItemType: 'estimate',
                  estimateId,
                  concern: jobPickerItem.notes || jobPickerItem.point_name,
                });
                setJobPickerItem(null);
                await refetch();
                onLineItemsChanged?.();
              }}
            />
          )}
        </>
      )}
    </Card>
  );
};

export default EstimateInspectionSection;
